import { moveStatisticsTabsToTopLevel, replaceFiltersWithText } from '../statistics';

const PAGE_CONTENT_HEIGHT_PX = 1866;

function mountHostAndSize(cloned: HTMLElement) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '0';
  host.style.top = '0';
  host.style.width = '1440px';
  host.style.opacity = '0';
  host.style.pointerEvents = 'none';
  host.style.zIndex = '0';
  host.appendChild(cloned);
  document.body.appendChild(host);

  cloned.style.minHeight = '1000px';
}

function injectBaseStyles(cloned: HTMLElement) {
  const style = document.createElement('style');
  style.textContent = `
    .statistics-public__export-pdf * {
      break-before: auto !important;
      break-after: auto !important;
      break-inside: auto !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      page-break-inside: auto !important;
    }
    .statistics-public__export-pdf .pdf-keep-with-next {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .statistics-public__export-pdf .html2pdf__page-break {
      break-before: page !important;
      page-break-before: always !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      display: block !important;
    }
    .statistics-public__export-pdf .html2pdf__page-break + * {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    .statistics-public__export-pdf .statistics-tabs,
    .statistics-public__export-pdf .statistics-tabs__body,
    .statistics-public__export-pdf .statistics-tabs__title {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
  `;
  cloned.prepend(style);
}

function normalizePageContainers(cloned: HTMLElement) {
  Array.from(cloned.querySelectorAll('.statistics-public__body, .container-page, .export-body, .big')).forEach((el) => {
    const n = el as HTMLElement;
    n.style.maxWidth = 'none';
    n.style.marginLeft = '0';
    n.style.marginRight = '0';
    if (!n.style.paddingLeft) n.style.paddingLeft = '16px';
    if (!n.style.paddingRight) n.style.paddingRight = '16px';
    n.style.width = '100%';
  });
  Array.from(cloned.querySelectorAll('.html2pdf__page-break')).forEach((n) => n.remove());
}

function placeFiltersAndFirstSection(cloned: HTMLElement) {
  const body = cloned.querySelector('.statistics-public__body') as HTMLElement | null;
  const filters = cloned.querySelector('.filter-factory, .statistics-public__filters') as HTMLElement | null;
  const sections = Array.from(cloned.querySelectorAll('.statistics-tabs')) as HTMLElement[];

  if (!body || !filters || !sections.length) return;

  const already =
    (!!filters.parentElement && filters.parentElement.classList.contains('pdf-keep-with-next')) ||
    (!!sections[0].parentElement && sections[0].parentElement.classList.contains('pdf-keep-with-next'));

  if (!already && sections[0].parentElement) {
    const group = document.createElement('div');
    group.className = 'pdf-keep-with-next';
    sections[0].parentElement.insertBefore(group, sections[0]);
    group.appendChild(filters);
    group.appendChild(sections[0]);

    const br = document.createElement('div');
    br.className = 'html2pdf__page-break';
    group.parentElement?.insertBefore(br, group.nextSibling);
  }
}

function glueTitlesWithContentAndBreak(cloned: HTMLElement) {
  const titles = Array.from(
    cloned.querySelectorAll('.statistics-tabs__title, h2.statistics-tabs__title'),
  ) as HTMLElement[];

  titles.forEach((title) => {
    const items =
      (title.parentElement?.querySelector('.statistics-tabs__items') as HTMLElement | null) ||
      (title.nextElementSibling as HTMLElement | null);
    if (items && !title.parentElement?.classList.contains('pdf-keep-with-next')) {
      const wrap = document.createElement('div');
      wrap.className = 'pdf-keep-with-next';
      title.parentElement!.insertBefore(wrap, title);
      wrap.appendChild(title);
      wrap.appendChild(items);
    }
  });

  const freshSections = Array.from(cloned.querySelectorAll('.statistics-tabs')) as HTMLElement[];
  for (let i = 1; i < freshSections.length; i++) {
    const sec = freshSections[i];
    const br = document.createElement('div');
    br.className = 'html2pdf__page-break';
    sec.parentElement?.insertBefore(br, sec);
    sec.style.marginTop = '0';
    sec.style.paddingTop = '0';
  }
}

function fixFiltersContainers(cloned: HTMLElement) {
  Array.from(cloned.querySelectorAll('.filter-factory, .filter-factory__filters')).forEach((el) => {
    const n = el as HTMLElement;
    n.style.flexWrap = 'wrap';
    n.style.overflow = 'visible';
  });
}

function fixCharts(cloned: HTMLElement) {
  Array.from(cloned.querySelectorAll('.vertical-chart-column__chart')).forEach((el) => {
    const n = el as HTMLElement;
    n.style.overflow = 'visible';
    n.style.maxWidth = '100%';
    n.style.width = '100%';
    n.style.background = 'transparent';
    n.style.border = 'none';
    n.style.padding = '16px';
  });
}

function allowBreaksInOversizedGroups(cloned: HTMLElement) {
  Array.from(cloned.querySelectorAll('.pdf-keep-with-next')).forEach((el) => {
    const group = el as HTMLElement;

    if (group.getBoundingClientRect().height > PAGE_CONTENT_HEIGHT_PX) {
      group.classList.remove('pdf-keep-with-next');
    }
  });
}

function trimTrailingSpace(cloned: HTMLElement) {
  let node: HTMLElement | null = cloned;

  while (node) {
    node.style.marginBottom = '0';
    node.style.paddingBottom = '0';
    node = node.lastElementChild as HTMLElement | null;
  }
}

function cleanupCanvasesAndReflow(cloned: HTMLElement) {
  Array.from(cloned.querySelectorAll('canvas')).forEach((c) => c.parentElement?.removeChild(c));
  void cloned.getBoundingClientRect();
}

export function statisticsPublicClonedNodeFormatter(cloned: HTMLElement) {
  mountHostAndSize(cloned);
  injectBaseStyles(cloned);
  normalizePageContainers(cloned);

  if (typeof (window as any).moveStatisticsTabsToTopLevel === 'function') {
    (window as any).moveStatisticsTabsToTopLevel(cloned);
  } else {
    moveStatisticsTabsToTopLevel && moveStatisticsTabsToTopLevel(cloned);
  }

  placeFiltersAndFirstSection(cloned);
  glueTitlesWithContentAndBreak(cloned);
  fixFiltersContainers(cloned);
  replaceFiltersWithText(cloned);
  fixCharts(cloned);
  trimTrailingSpace(cloned);
  allowBreaksInOversizedGroups(cloned);
  cleanupCanvasesAndReflow(cloned);
}
